"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { useZocdocBooking } from "@/hooks/useZocDocBooking";

const Hero: React.FC = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isMobile, setIsMobile] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { openBooking } = useZocdocBooking();

  // Optimized mobile detection with minimal impact
  useEffect(() => {
    // Set initial value immediately
    setIsMobile(window.innerWidth < 768);
    
    // Debounced resize handler to reduce main thread work
    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setIsMobile(window.innerWidth < 768);
      }, 100); // Reduced debounce time
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  // Optimized video loading with better fallback
  useEffect(() => {
    if (isMobile) return;

    const video = videoRef.current;
    if (!video) return;

    // Load video immediately for better user experience
    const handleLoadedData = () => {
      setVideoLoaded(true);
      // Ensure video plays if autoplay failed
      video.play().catch(() => {
        console.log('Video autoplay prevented by browser');
      });
    };
    
    const handleError = () => setVideoError(true);

    video.addEventListener('loadeddata', handleLoadedData, { passive: true });
    video.addEventListener('error', handleError, { passive: true });

    // Load video immediately
    video.load();

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
    };
  }, [isMobile]);

  const handleBookOnlineClick = useCallback(() => {
    openBooking();
  }, [openBooking]);

  return (
    <div className="relative w-full h-[100vh] overflow-hidden">
      {/* Background Image - optimized for LCP */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('/images/custom_images_banner.webp')"
        }}
      />

      {/* Video Background - optimized for autoplay */}
      {!isMobile && (
        <video
          ref={videoRef}
          muted
          autoPlay
          loop
          playsInline
          preload="metadata"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            videoLoaded && !videoError ? 'opacity-100' : 'opacity-0'
          }`}
          onLoadedData={() => setVideoLoaded(true)}
          onError={() => setVideoError(true)}
          aria-label="Background video of Smile4Me dental office"
        >
          <source
            src="https://smile4me-images.s3.us-east-2.amazonaws.com/videos/smile4me-new.webm"
            type="video/webm"
          />
          <source
            src="https://smile4me-images.s3.us-east-2.amazonaws.com/videos/smile4me-new.mp4"
            type="video/mp4"
          />
        </video>
      )}

      {/* Minimal overlay */}
      <div className="absolute inset-0 bg-black/20 z-10"></div>

      {/* Content - render immediately with no animations that delay LCP */}
      <div className="relative z-20 flex flex-col items-center lg:items-start justify-center h-full lg:px-20 px-4 pt-32 md:pt-20 lg:pt-0">
        <div className="w-full max-w-lg text-center bg-black/30 rounded-lg py-6 px-4">
          {/* Critical LCP element - no animations, render immediately */}
          <h1
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white drop-shadow-lg"
            style={{
              // Inline critical styles to avoid render blocking
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              lineHeight: '1.15'
            }}
          >
            Your Trusted Dentist in Astoria, Queens, NY
          </h1>
          <p
            className="mt-4 text-base md:text-lg text-white/95 leading-relaxed"
            style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
          >
            Astoria&apos;s full-service dental practice. Same-day emergency
            appointments available and flexible financing with 0% interest.
          </p>
          
          {/* Mobile additional content */}
          {isMobile && (
            <div className="mt-6 space-y-4">
              <p className="text-3xl font-medium bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent [text-shadow:1px_1px_2px_rgba(0,0,0,0.3)]">
                Excellence in Dental Care
              </p>
              <p className="text-base text-white/80 leading-relaxed">
                Providing comprehensive dental services with cutting-edge technology and personalized care for the whole family.
              </p>
              <div className="flex justify-center items-center space-x-6 text-white/70 text-sm">
                <div className="text-center">
                  <div className="font-bold bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent text-2xl">15+</div>
                  <div>Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="font-bold bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent text-2xl">5000+</div>
                  <div>Happy Patients</div>
                </div>
                <div className="text-center">
                  <div className="font-bold bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent text-2xl">⭐⭐⭐⭐⭐</div>
                  <div>Top Rated</div>
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={handleBookOnlineClick}
              className="bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] hover:from-[#ECD05B] hover:via-[#AE7D2F] hover:to-[#ECD05B] text-white text-lg font-bold py-3 px-8 rounded-lg shadow-lg hover:scale-105 hover:shadow-inner transition-all duration-300 ease-in-out tracking-wide [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)] cursor-pointer min-h-[44px] min-w-[44px]"
              aria-label="Book an appointment online"
              type="button"
            >
              Book Online
            </button>
            <a
              href="/our-office"
              className="border-2 border-[#AE7D2F] hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] text-white text-lg font-bold py-3 px-8 rounded-lg shadow-lg hover:scale-105 hover:shadow-inner transition-all duration-300 ease-in-out tracking-wide [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)] cursor-pointer min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
              aria-label="Learn about our office"
            >
              Our Office
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;